<template>
  <div class="container">
    <LmLoading v-if="isLoading" absolute />
    <template>
      <h1 v-if="pageTitleData && !disablePageTitleData">{{formTitle || pageTitleData}}</h1>
      <div class="row">
        <div class="logo-container">
          <b-img class="logo-image" center thumbnail :src="logoPath"></b-img>
        </div>
        <template v-for="(formRow, index) in formElements" >
          <component
            :id="`${formRow.componentName}-${index}`"
            :ref="`componentRef${index}`"
            :key="index"
            :class="formRow.class"
            :is="formRow.componentName"
            :showEditButton="isOnPreviewMode"
            :visibility="formRow.visibilityDependOnConditions"
            v-bind="formRow.componentProps"
            :modelName="formRow.formObjectToSendToServerProperty"
            v-model="formObjectToSendToServer[formRow.formObjectToSendToServerProperty]"
            @edit-button-clicked="click(formRow, index)"
            @onUpdate="formObjectToSendToServer[formRow.formObjectToSendToServerProperty] = $event"
            @uploadProcessing="uploadProcessing = $event"
            @mentions-list="$emit('mentions-list', $event)"
            @editor-change="onEditorChange(formRow, $event)"
            @editor-change-on-subelement="onEditorChangeOnSubElement(formRow, $event)"
            :editor="formRowEditor(formRow)"
            :editors="formRow.componentName === 'LmRepeatFields' ? editors : null"
          />
        </template>
        <h4 v-if="emptyFormFields" class="text-center">No hay campos para mostrar</h4>
      </div>
    </template>
  </div>
</template>

<script>
import commonsApi from '@/api/commons'
import _ from 'lodash'

// Components
import LmForm from '@/components/form/LmForm'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmNumberInput from '@/components/form/elements/LmNumberInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmFormSubtitles from '@/components/form/elements/LmFormSubtitles'
import LmFormEmptySpace from '@/components/form/elements/LmFormEmptySpace'
import LmFormSeparator from '@/components/form/elements/LmFormSeparator'

// QMS Components
import LmQmsButton from '@/components/form/elements/LmQmsButton'
import LmQmsNumberInput from '@/components/form/elements/LmQmsNumberInput'
import LmQmsWatch from '@/components/form/elements/LmQmsWatch'

// Utils
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import lmToasts from '@/mixins/lmToasts.js'

export default {
  provide () {
    return {
      context: this.formObjectToSendToServer,
      isOnPreviewMode: this.isOnPreviewMode,
      isOnEditMode: this.isParentOnEditMode
    }
  },
  inject: {
    document: {
      type: Object,
      default: null
    }
  },
  mixins: [lmToasts],
  components: {
    LmForm,
    LmButton,
    LmTextInput,
    LmEmailInput,
    LmNumberInput,
    LmFormSubtitles,
    LmFormEmptySpace,
    LmFormSeparator,
    LmNumberInputV2,
    LmQmsButton,
    LmQmsNumberInput,
    LmQmsWatch
  },
  computed: {
    emptyFormFields () {
      return !this.formElements.some(el => this.isVisible(el)) && this.compactMode
    },
    logoPath () {
      if (this.logo?.path) {
        return process.env.VUE_APP_API_URL_STATIC_ASSETS + this.logo.path
      }
      return null
    }
  },
  data () {
    return {
      isLoading: false,
      componentIsDisabled: {},
      registerOfDataWatchDinamically: {},
      pageTitleData: '',
      messageAfterPOSTorPUT: 'Guardado',
      pathToRedirectAfterPOSTorPUT: '/',
      apiRestForHandleAllHttpMethods: '',
      formObjectToSendToServer: {},
      formElements: [], // el array con todos elementos que componen el formulario
      isOnEditMode: false,
      uploadProcessing: false,
      firstLoad: true
    }
  },
  props: {
    logo: {
      type: Object
    },
    compactMode: {
      type: Boolean,
      default: false
    },
    isOnPreviewMode: {
      type: Boolean,
      default: false
    },
    getComponents: {
      type: Object,
      default: () => ({})
    },
    endpointUriwhereGetComponents: {
      type: String,
      default: ''
    },
    endpointUriWhereGetValuesToLoadInEditMode: {
      type: String,
      default: ''
    },
    contentTemplateData: {
      type: Object,
      default: () => ({})
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    disablePageTitleData: {
      type: Boolean,
      default: false
    },
    formTitle: {
      type: String,
      default: null
    },
    formValues: {
      type: Object,
      default: null
    },
    editors: {
      type: Array,
      default: () => []
    },
    isParentOnEditMode: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onEditorChange (formRow, event) {
      this.$emit('editor-change', { editor: event, element: formRow })
    },
    onEditorChangeOnSubElement (formRow, event) {
      this.$emit('editor-change', { editor: event.editor, element: formRow, subelement: event.subelement })
    },
    formRowEditor (formRow) {
      return this.editors.find(el => el.element === formRow.formObjectToSendToServerProperty) || {}
    },
    isVisible (formRow) {
      if (
        this.compactMode &&
        (this.isDisabledByEditor(formRow) || this.isDisabledInDocumentCtx(formRow))
      ) {
        return false
      }

      return true
    },
    isDisabledInDocumentCtx (formRow) {
      if (this.document === null) return false

      if (this.document.isStarted) {
        if (!this.editors.some(el => el.element === formRow.formObjectToSendToServerProperty)) {
          return true
        }
      }
      return false
    },
    isDisabledByEditor (formRow) {
      if (this.editors.length) {
        if (formRow.componentName !== 'LmRepeatFields') {
          const editor = this.formRowEditor(formRow)
          if (Object.keys(editor).length) {
            if (this.$store.getters['session/userInfo'].id !== editor?.id) {
              return true
            }
            return false
          }
        }
        if (formRow.componentName === 'LmRepeatFields') {
          return !this.editors.some(el => {
            return (
              el.element === formRow.formObjectToSendToServerProperty &&
              el.id === this.$store.getters['session/userInfo'].id
            )
          })
        }
      }
      return false
    },
    onEditMode (event) {
      this.isOnEditMode = event || false
    },
    shouldCreatePortal (componentName) {
      return componentName !== 'LmComment'
    },
    async initializeComponent () {
      // Si this.getComponents tiene elementos formbuilder está operando en modo edicion
      // del formulario, ej /FormBuilderUi/edit/[id_de_un_formbuilder]
      if (Object.keys(this.getComponents).length) {
        this.formElements = _.cloneDeep(this.getComponents.formElements)
      } else {
        // Formbuilder esta operando en modo visualizacion
        // Obtenemos toda la informacion del formulario desde la API
        const endPoint = await commonsApi.axiosGetWrapper(this.endpointUriwhereGetComponents)
        this.pageTitleData = endPoint.data.pageTitleData
        this.messageAfterPOSTorPUT = endPoint.data.messageAfterPOSTorPUT
        this.pathToRedirectAfterPOSTorPUT = endPoint.data.pathToRedirectAfterPOSTorPUT
        this.apiRestForHandleAllHttpMethods = endPoint.data.apiRestForHandleAllHttpMethods
        this.formElements = endPoint.data.formElements
      }
      // Los super power hijos no cargo los valores
      for (const el of this.formElements) {
        if (el?.childrenOf && el?.childrenOf !== null) {
          el.componentProps.disabled = true
        }
      }
      this.$emit('formBuilderInitialized')
    },
    /**
     * Obtiene desde el backend los datos asociados al registro que se está editando
     *
     * activeEditMode es parte del mixin lmFormMixinV2 que se carga en el componente LmForm
     *
     * emite formLoadingComplete
     */
    async setForm () {
      if (this.endpointUriWhereGetValuesToLoadInEditMode) {
        await this.$refs.lmFormRef.activeEditMode({
          path: this.endpointUriWhereGetValuesToLoadInEditMode,
          id: '',
          paramsMode: true }, 'formObjectToSendToServer')
        this.$emit('formLoadingComplete')
      }

      if (this.isOnEditMode && this.formValues) {
        for (const key in this.formValues) {
          if (this.formObjectToSendToServer.hasOwnProperty(key)) {
            this.formObjectToSendToServer[key] = this.formValues[key]
          }
        }
      }
    },
    async click (formRow, index) {
      if (this.isOnPreviewMode) {
        this.$emit('openEditFormElement', index)
      }
      try {
        if (formRow?.actions) {
          switch (formRow?.actions?.click?.type) {
            case 'submit':
              // Creo el objeto para enviar al server + custom params
              const params = formRow?.actions?.click?.params
              const dataToSendToServer = params ? Object.assign({}, this.formObjectToSendToServer, params) : this.formObjectToSendToServer

              if (this.endpointUriWhereGetValuesToLoadInEditMode) {
                // Verifico los permisos del usuario para actualizar
                // if (!this.userPrivileges?.allowUpdate) {
                //   return this.lmToastDangerMessage('Lo sentimos, no tiene permisos para realizar esta acción.', 'Error al actualizar')
                // }
                this.$refs.lmFormRef.showLoading()
                const pathUpdate = formRow?.actions?.click?.arguments?.pathUpdate || this.apiRestForHandleAllHttpMethods
                await commonsApi.axiosPutWrapper(pathUpdate + this.$route.params.id, dataToSendToServer)
                this.lmToastSuccessMessage(this.messageAfterPOSTorPUT || 'Se guardó correctamente', 'Acción exitosa')
                if (this.pathToRedirectAfterPOSTorPUT) {
                  this.$router.push(this.pathToRedirectAfterPOSTorPUT)
                }
              } else {
                // Verifico los permisos del usuario para guardar
                // if (!this.userPrivileges?.allowCreate) {
                //   return this.lmToastDangerMessage('Lo sentimos, no tiene permisos para realizar esta acción.', 'Error al guardar')
                // }
                this.$refs.lmFormRef.showLoading()
                await commonsApi.axiosPostWrapper(formRow?.actions?.click?.arguments?.path || this.apiRestForHandleAllHttpMethods, dataToSendToServer)
                this.lmToastSuccessMessage(this.messageAfterPOSTorPUT || 'Se guardó correctamente', 'Acción exitosa')
                if (this.pathToRedirectAfterPOSTorPUT) {
                  this.$router.push(this.pathToRedirectAfterPOSTorPUT)
                }
              }
              break
            case 'cancel':
              this.$router.go(-1)
              break
            case 'delete':
              if (formRow?.actions?.click?.arguments?.deleteMethod === 'put') {
              // Borrado lógico
                const propertyToUpdate = {}
                propertyToUpdate[formRow?.actions?.click?.arguments?.logicDeleteProperty] = formRow?.actions?.click?.arguments?.logicDeleteValue
                propertyToUpdate.id = this.$route.params.id
                await commonsApi.axiosPutWrapper(formRow?.actions?.click?.arguments?.pathDelete || this.apiRestForHandleAllHttpMethods, propertyToUpdate)
                this.lmToastSuccessMessage('Eliminado correctamente.', 'Acción exitosa')
                this.$router.push(this.pathToRedirectAfterPOSTorPUT)
              } else if (formRow?.actions?.click?.arguments?.deleteMethod === 'delete') {
              // Borrado permanente
                await commonsApi.axiosDeleteWrapper(formRow?.actions?.click?.arguments?.pathDelete || this.apiRestForHandleAllHttpMethods + this.$route.params.id)
                this.lmToastSuccessMessage('Eliminado correctamente.', 'Acción exitosa')
                this.$router.push(this.pathToRedirectAfterPOSTorPUT)
              }
              break
            default:
              break
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$refs.lmFormRef.hideLoading()
      }
    },
    setFormElements () {
      this.formElements.forEach((component, index) => {
        if (component.formObjectToSendToServerProperty) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, '')
        }
        if (component.componentName === 'LmSuperPowerSelectV2') {
          // this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, { id: '', name: '' })
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, [])
        }
        if (component.componentName === 'lmSuperPowerSelectV2Multiple' && component?.componentProps.multiple) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, [])
        }
        if (component.componentName === 'LmLeaFletMap') {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, {})
        }
        if (
          (component.componentName === 'LmDate' || component.componentName === 'LmDateTime') &&
        this.formElements[index].componentProps &&
        this.formElements[index].componentProps.config
        ) {
          this.formElements[index].componentProps.config.locale = Spanish
        }
        if (component?.componentProps?.urlPathFromGetOptions) {
          this.formElements[index].componentProps.urlPathFromGetOptions = component?.componentProps?.urlPathFromGetOptions
        }

        // Nota: Esta línea se encarga de setear los valores por defecto, si el
        // componente tiene "componentProps.defaultValue" lo uso para setear los valores por defecto
        // Esto es vital para el campo LmReaderInput o editor con variables, ya que sin este seteo
        // no va a contar con un valor por defecto para renderizar el editor con variables
        if (component?.componentProps?.defaultValue && !this.isOnEditMode) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, component.componentProps.defaultValue)
        }
        if (component?.componentName === 'LmCheckbox' && !this.isOnEditMode) {
          if (component?.componentProps?.type === 'single') {
            if (component?.componentProps?.singleSelected) {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.checkValue || true
              )
            } else {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.uncheckedValue || false
              )
            }
          } else if (component?.componentProps?.type === 'multiple') {
            if (component?.componentProps?.options?.length) {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.options?.filter(el => el.checked).map(item => item.value)
              )
            }
          }
        }
      })
    },
    submit () {
      this.$emit('formObjectToSendToServerChange', this.formObjectToSendToServer)
    }
  },
  watch: {
    endpointUriWhereGetValuesToLoadInEditMode () {
      // Este valor puede cambiar si al invocar al formbuilder se pasa como un valor dinamico
      // LmFormBuilderPreview. es un ejemplo de este caso
      this.setForm()
    },
    endpointUriwhereGetComponents () {
      this.initializeComponent()
    },
    // Permite cargar contenido desde un template de contenido en un formbuilder hibrido
    contentTemplateData: {
      deep: true,
      handler (val) {
        // Si tengo contenido, lo cargo
        if (Object.keys(val).length && !Array.isArray(val)) {
          for (const key in val) {
            if (this.formObjectToSendToServer.hasOwnProperty(key)) {
              this.formObjectToSendToServer[key] = val[key]
            }
          }
        } else {
          // Si apreto "x" en el SP de content template
          this.setFormElements()
        }
      }
    },
    'getComponents.pageTitleData' () {
      this.pageTitleData = this.getComponents.pageTitleData
    },
    'getComponents.apiRestForHandleAllHttpMethods' () {
      this.apiRestForHandleAllHttpMethods = this.getComponents.apiRestForHandleAllHttpMethods
    },
    'getComponents.formElements': {
      handler: function () {
        this.formElements = _.cloneDeep(this.getComponents.formElements)
        this.setFormElements()
      },
      deep: true
    },
    formObjectToSendToServer: {
      deep: true,
      handler (val) {
        // Me aseguro de no enviar mal los "names" de los campos
        for (const item in val) {
          if (!this.formElements.some(el => el.formObjectToSendToServerProperty === item)) {
            delete val[item]
          }
        }

        // Horrible forma de persistir los datos del formbuilder cuando se crea un documento
        // desde un proceso
        if (this.formValues && this.firstLoad) {
          for (const key in this.formValues) {
            if (val.hasOwnProperty(key)) {
              val[key] = this.formValues[key]
            }
          }
          this.firstLoad = false
        }

        this.$emit('formObjectToSendToServerChange', val)
      }
    },
    formValues: {
      deep: true,
      handler (val) {
        if (val && Object.keys(val).length) {
          for (const key in val) {
            if (this.formObjectToSendToServer.hasOwnProperty(key)) {
              this.formObjectToSendToServer[key] = val[key]
            }
          }
        }
      }
    }
  },
  async created () {
    // NOTA. con el tema de los editores, es importante saber si el form esta en
    // modo edición o modo nuevo
    if (this.isParentOnEditMode) {
      this.isOnEditMode = true
    }

    try {
      this.isLoading = true
      this.$emit('is-loading', this.isLoading)
      await this.initializeComponent()
      this.setFormElements()
      await this.setForm()

      // NOTA: Esto no es generico, solo sirve para el workflow de documentos
      if (this.document?.lumbreVersion === 1) {
        this.formElements.forEach(el => {
          if (el?.componentProps?.isEditableByPickedUser) {
            el.componentProps.isEditableByPickedUser = false
          }
        })
      } else if (this.document?.lumbreVersion === 2) {
        if (!this.isOnEditMode) {
          const user = this.$store.getters['session/userInfo']
          let hideCollaborators = true
          this.formElements.forEach((el) => {
            if (el?.componentProps?.isEditableByPickedUser) {
              hideCollaborators = false
              this.$emit('editor-change', {
                editor: {
                  id: user.id,
                  name: `${user.firstname} ${user.lastname}`
                },
                element: el
              })
            }
            if (el.componentName === 'LmRepeatFields') {
              el.componentProps.initialItems.formElements.forEach((iel) => {
                if (iel.componentProps?.isEditableByPickedUser) {
                  hideCollaborators = false
                  this.$emit('editor-change', {
                    editor: {
                      id: user.id,
                      name: `${user.firstname} ${user.lastname}`
                    },
                    element: el,
                    subelement: `0.${iel.formObjectToSendToServerProperty}`
                  })
                }
              })
            }
          })
          if (hideCollaborators) {
            this.$emit('hide-collaborators-workflow')
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
      this.$emit('is-loading', this.isLoading)
    }
  }
}
</script>

<style lang="sass" scoped>
  .logo-container
    padding-botton: 10px
  .logo-image
    position: absolute
    right: 10px
    top: 10px
    max-width: auto
    height: 60px
</style>
